import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { CREDENTIAL_VALIDATION_POLL_INTERVAL_MS } from '@/config/constants'
import { credentialKeys } from '@/lib/queryKeys'
import {
  createEcwCredentialValidation,
  fetchEcwCredentialValidation,
  promoteEcwCredential,
} from './api'

export function useEcwCredentialValidationQuery(
  enabled: boolean,
  candidateVersion: number | null | undefined
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: credentialKeys.validation(
      organization?.id,
      'ecw',
      candidateVersion
    ),
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      if (candidateVersion == null) {
        throw new Error('No candidate credential version available')
      }
      return fetchEcwCredentialValidation(token, candidateVersion, signal)
    },
    enabled: enabled && !!organization?.id && candidateVersion != null,
    refetchInterval: (query) =>
      query.state.data?.state === 'pending' ||
      query.state.data?.state === 'running'
        ? CREDENTIAL_VALIDATION_POLL_INTERVAL_MS
        : false,
    retry: false,
  })
}

export function useEcwCredentialValidationMutation(
  candidateVersion: number | null | undefined
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return createEcwCredentialValidation(token)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        credentialKeys.validation(organization?.id, 'ecw', candidateVersion),
        data
      )
    },
  })
}

export function useEcwCredentialPromotionMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return promoteEcwCredential(token)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: credentialKeys.status(organization?.id, 'ecw'),
      })
      void queryClient.invalidateQueries({
        queryKey: credentialKeys.validationAll(organization?.id, 'ecw'),
      })
    },
  })
}
